/**
 * Home Page Intro Animation
 *
 * Handles the initial fade-in animation for the home page content.
 * Wraps the two-column layout and animates both columns in with stagger.
 * After ~3.5s of inactivity, shows a scroll hint bounce animation.
 */
import { gsap } from "gsap";

export default () => ({
  _introTimeline: null,
  _hintTimeline: null,
  _scrollHintTimeout: null,
  _scrollHintPlayed: false,
  _scrollCancelHandlers: [],
  _leftColumn: null,
  _rightColumn: null,
  _leftContent: null,
  _rightContent: null,

  init() {
    // Skip animation if coming from internal navigation
    try {
      const isInternalNavigation =
        document.referrer &&
        new URL(document.referrer).origin === window.location.origin;
      if (isInternalNavigation) {
        // Make content visible immediately without animation
        const content = this.$el.querySelectorAll("[data-column-content]");
        content.forEach((el) => (el.style.visibility = "visible"));
        return;
      }
    } catch {
      // Invalid referrer URL, continue with animation
    }

    this._rightContent = this.$el.querySelector('[data-column-content="right"]');
    this._leftContent = this.$el.querySelector('[data-column-content="left"]');

    if (!this._rightContent || !this._leftContent) return;

    // Store column refs for scroll listeners
    this._rightColumn = this.$el.querySelector(".scroll-column.bg-nf-blue");
    this._leftColumn = this.$el.querySelector(".scroll-column.bg-nf-black");

    // Animate right content first, then left with overlap
    // autoAlpha handles both opacity and visibility (allows lazy images to preload)
    this._introTimeline = gsap
      .timeline({
        defaults: { duration: 1, ease: "power2.out" },
        onComplete: () => this._setupScrollHint(),
      })
      .fromTo(this._rightContent, { autoAlpha: 0, y: -120 }, { autoAlpha: 1, y: 0 })
      .fromTo(this._leftContent, { autoAlpha: 0, y: -120 }, { autoAlpha: 1, y: 0 }, "-=0.5");
  },

  _setupScrollHint() {
    this._scrollHintTimeout = setTimeout(() => {
      if (!this._scrollHintPlayed) this._playScrollHint();
    }, 3500);

    // Cancel on scroll
    const cancelHint = () => this._cancelScrollHint();

    // Listen to scroll on both columns
    if (this._rightColumn) {
      this._rightColumn.addEventListener("scroll", cancelHint, {
        passive: true,
        once: true,
      });
      this._scrollCancelHandlers.push([this._rightColumn, cancelHint]);
    }
    if (this._leftColumn) {
      this._leftColumn.addEventListener("scroll", cancelHint, {
        passive: true,
        once: true,
      });
      this._scrollCancelHandlers.push([this._leftColumn, cancelHint]);
    }
  },

  _playScrollHint() {
    this._scrollHintPlayed = true;

    if (!this._leftContent || !this._rightContent) return;

    // Bounce sequence: left lifts, bounces down, then right follows
    // Animate content wrappers (not columns) so backgrounds stay in place
    this._hintTimeline = gsap
      .timeline()
      .to(this._leftContent, { y: -60, duration: 0.3, ease: "power2.out" })
      .to(this._leftContent, { y: 0, duration: 0.5, ease: "bounce.out" })
      .to(this._rightContent, { y: -60, duration: 0.3, ease: "power2.out" }, "<0.15")
      .to(this._rightContent, { y: 0, duration: 0.5, ease: "bounce.out" });
  },

  _cancelScrollHint() {
    if (this._scrollHintTimeout) {
      clearTimeout(this._scrollHintTimeout);
      this._scrollHintTimeout = null;
    }
    this._scrollHintPlayed = true;
  },

  destroy() {
    // Cancel pending hint
    this._cancelScrollHint();

    // Kill any running GSAP animations
    if (this._introTimeline) this._introTimeline.kill();
    if (this._hintTimeline) this._hintTimeline.kill();
    if (this._leftContent) gsap.killTweensOf(this._leftContent);
    if (this._rightContent) gsap.killTweensOf(this._rightContent);

    // Remove scroll listeners
    this._scrollCancelHandlers.forEach(([el, handler]) => {
      el.removeEventListener("scroll", handler);
    });
    this._scrollCancelHandlers = [];
  },
});
